import React, { useState } from 'react';
import { LegacyCheckoutPreviewHost } from 'components/OfferingPreview/LegacyCheckoutPreviewHost';
import sampleInputs from 'data/legacyCheckoutPreviewSamples.json';
import type { InstallationDraft } from 'types/installationPresentation';
import {
  createInstallationPresentation, parseInstallationPresentation,
  INSTALLATION_PRESENTATION_CATALOG,
  APPROVED_CHECKOUT_TITLE_PRESETS, getApprovedCheckoutTitlePreset,
} from 'ui.shipaid.com/renderers/checkout-plus-legacy';
import type { PresentationTokens } from 'ui.shipaid.com/renderers/checkout-plus-legacy';
import { upgradeInstallationPreferences } from 'utils/installationPresentation';
import { resolveCustomizedInstallationSample } from 'utils/installationSamplePresentation';
import { CHECKOUT_TITLE_REVIEW_MESSAGE, hasApprovedCheckoutTitles, withApprovedCheckoutTitle } from 'utils/checkoutTitlePolicy';
import sampleProduct from 'assets/images/sample-product.jpg';

interface Props {
  preferences: InstallationDraft;
  disabled: boolean;
  onChange: (preferences: InstallationDraft) => void;
  invalidTitle: string | null;
  onInvalidTitleChange: (title: string | null) => void;
}

const control = 'mt-1 block min-h-[44px] w-full rounded-lg border border-slate-300 bg-white p-2 text-sm focus-visible:ring-2 focus-visible:ring-blue-600 disabled:opacity-50';

export function ApprovedCheckoutTitleSelect({ preferences, disabled, onChange }: Pick<Props, 'preferences' | 'disabled' | 'onChange'>) {
  const needsReview = !hasApprovedCheckoutTitles(preferences);
  const title = preferences.schemaVersion === 2
    ? preferences.appearance.localeOverrides['en-US']?.title ?? preferences.appearance.copy.title : null;
  const selected = getApprovedCheckoutTitlePreset(title);
  return <>
    <label className="block text-sm font-medium">Checkout wording
      <select className={control} disabled={disabled} value={needsReview ? 'choose-approved' : selected?.title === APPROVED_CHECKOUT_TITLE_PRESETS[0].title ? '' : selected?.title ?? ''}
        aria-describedby="checkout-title-guidance" aria-invalid={needsReview || undefined}
        onChange={event => {
          if (disabled) return;
          const next = withApprovedCheckoutTitle(preferences, event.target.value === '' ? null : event.target.value);
          if (next) onChange(next);
        }}>
        {needsReview && <option value="choose-approved" disabled>Choose an approved title</option>}
        {APPROVED_CHECKOUT_TITLE_PRESETS.map((preset, index) => (
          <option key={preset.title} value={index === 0 ? '' : preset.title}>{preset.title}</option>
        ))}
      </select>
    </label>
    <p id="checkout-title-guidance" className="mt-1 text-xs text-slate-600">Choose a heading. Checkout buttons keep the same compact wording.</p>
    <a href="https://calendly.com/shipaid-support/expert-call" target="_blank" rel="noopener noreferrer"
      className="mt-2 inline-block text-sm font-medium text-blue-700 underline focus-visible:ring-2 focus-visible:ring-blue-600">
      Request custom wording — book a call
    </a>
  </>;
}

export default function CheckoutCustomization({ preferences, disabled, invalidTitle, onChange, onInvalidTitleChange }: Props) {
  const [sampleAction, setSampleAction] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const titleNeedsReview = invalidTitle !== null || !hasApprovedCheckoutTitles(preferences);
  const appearance = preferences.schemaVersion === 2 ? preferences.appearance
    : createInstallationPresentation(preferences.appearance.mode === 'inherit-theme' ? 'match-theme' : 'preserve-existing');
  const tokens = appearance.tokens ?? INSTALLATION_PRESENTATION_CATALOG.presets[appearance.mode === 'match-theme'
    ? 'match-theme' : 'preserve-existing'] as PresentationTokens;
  const result = resolveCustomizedInstallationSample(sampleInputs[0], preferences, {
    isGreen: false, isFreeGiftEnabled: false, detailsOpen, hasProtectionInCart: false,
    toggleLoading: false, benefitsId: 'guided-checkout-benefits', controlLayout: 'two-buttons',
  });
  const changeAppearance = (next: unknown) => {
    if (disabled) return;
    const parsed = parseInstallationPresentation(next);
    if (!parsed) return;
    const draft = preferences.schemaVersion === 2 ? preferences : upgradeInstallationPreferences(preferences);
    if (draft) onChange({ ...draft, appearance: parsed });
    setSampleAction('');
  };
  const setToken = <K extends keyof PresentationTokens>(key: K, value: PresentationTokens[K]) => {
    changeAppearance({ ...appearance, mode: 'custom', tokens: { ...tokens, [key]: value } });
  };

  return <div className="grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
    <fieldset disabled={disabled} className="min-w-0 space-y-4">
      <legend className="mb-3 text-base font-semibold text-slate-900">Make it yours</legend>
      <ApprovedCheckoutTitleSelect preferences={preferences} disabled={disabled} onChange={next => {
        onInvalidTitleChange(null); onChange(next); setSampleAction('');
      }} />
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm font-medium">Button shape
          <select className={control} value={tokens.shape}
            onChange={event => setToken('shape', event.target.value as PresentationTokens['shape'])}>
            <option value="square">Square</option><option value="rounded">Rounded</option><option value="pill">Pill</option>
          </select>
        </label>
        <div className="min-w-0">
          <label className="block text-sm font-medium">Font
            <select className={control} value={tokens.font} aria-describedby="guided-font-selection"
              onChange={event => setToken('font', event.target.value as PresentationTokens['font'])}>
              <option value="theme" label="Theme font" aria-label="Theme font sample">Theme font sample</option>
              <option value="system">System font</option>
            </select>
          </label>
          <span id="guided-font-selection" className="mt-1 block whitespace-normal break-words text-xs font-normal text-slate-600">
            {tokens.font === 'theme' ? 'Theme font sample' : 'System font'}
          </span>
        </div>
        <label className="block text-sm font-medium">Spacing
          <select className={control} value={tokens.spacing}
            onChange={event => setToken('spacing', event.target.value as PresentationTokens['spacing'])}>
            <option value="compact">Compact</option><option value="comfortable">Comfortable</option>
          </select>
        </label>
        <label className="block text-sm font-medium">SHIPAID logo
          <select className={control} value={tokens.logo}
            onChange={event => setToken('logo', event.target.value as PresentationTokens['logo'])}>
            <option value="default">Show</option><option value="hidden">Hide</option>
          </select>
        </label>
      </div>
      <button type="button" className="min-h-[44px] text-sm font-medium text-blue-700 underline focus-visible:ring-2 focus-visible:ring-blue-600 disabled:opacity-50"
        onClick={() => { if (disabled) return; onInvalidTitleChange(null);
          changeAppearance(createInstallationPresentation('preserve-existing')); }}>Reset design</button>
    </fieldset>
    <div className="min-w-0 rounded-xl bg-slate-50 p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><p className="text-base font-semibold text-slate-900">Your Checkout+ sample</p>
        <span className="rounded-full bg-white px-3 py-1 text-xs text-slate-500">Sample preview · draft appearance</span></div>
      <div className="mb-5 flex items-center gap-3 rounded-lg bg-white p-3">
        <img src={sampleProduct} alt="Sample product for the Checkout+ cart preview" className="h-16 w-16 rounded-lg object-cover" />
        <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800">Sample cart</p><p className="mt-1 text-xs text-slate-500">One sample item · USD</p></div>
        <p className="text-sm font-semibold text-slate-900">$100.00</p>
      </div>
      {titleNeedsReview ? <p role="alert" className="text-sm text-red-700">{CHECKOUT_TITLE_REVIEW_MESSAGE}</p>
        : result.status === 'available' ? <LegacyCheckoutPreviewHost snapshot={result.snapshot} actions={{
        onToggleDetails: () => setDetailsOpen(value => !value),
        onSeeDetails: () => setSampleAction('Sample details selected. No external page opened.'),
        onPrimaryClick: () => setSampleAction('Sample guarantee selected. No purchase or checkout opened.'),
        onSecondaryClick: () => setSampleAction('Sample regular checkout selected. No checkout opened.'),
      }} /> : <p role="alert" className="text-sm text-amber-800">{result.message}</p>}
      <p className="mt-3 text-xs text-slate-600">Sample cart: $100 USD · Guarantee: $3.10 USD. Your store’s price and installation are unchanged.</p>
      {sampleAction && <p role="status" className="mt-2 text-xs text-slate-600">{sampleAction}</p>}
    </div>
  </div>;
}
